'use client'

import { useState, useEffect } from 'react'
import { useAtom } from 'jotai'

import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  DatePicker,
  Dialog,
  Group,
  Heading,
  Popover,
  DateValue,
} from 'react-aria-components'
import { CalendarDate, isToday } from '@internationalized/date'
import Icon from '@/components/ui/icon'
import { startDateAtom } from '@/components/filters/state'

type FilterDatepickerProps = {
  heading: string
}

export default function FilterDatepicker({ heading }: FilterDatepickerProps): React.ReactNode {
  const [value, setValue] = useState<DateValue | null>(null)
  const [state, stateCallback] = useAtom<string | null>(startDateAtom) // This could be later refactored out to allow for an end date field

  // Synchronize local value with incoming state prop
  useEffect(() => {
    if (state) {
      // Convert Unix timestamp back to CalendarDate
      const date = new Date(state * 1000)
      const calendarDate = new CalendarDate(
        date.getFullYear(),
        date.getMonth() + 1, // CalendarDate uses 1-based months
        date.getDate(),
      )
      setValue(calendarDate)
    } else {
      setValue(null)
    }
  }, [state])

  return (
    <div className="mt-[-1px] w-full border-t border-grey-300 p-m ms:p-s">
      {heading ? <h6 className="mb-xs">{heading}</h6> : null}

      <DatePicker
        value={value}
        className="flex items-center gap-x-16"
        onChange={(value) => {
          const { day, month, year } = value || {}
          const date = day && month && year ? new Date(year, month - 1, day) : null
          const unixTimestamp = date ? Math.floor(date.getTime() / 1000) : null

          setValue(value)
          stateCallback(unixTimestamp)
        }}>
        <Group className="w-full">
          <Button className="group block w-full border p-xs">
            <div className="flex items-center justify-between">
              <span>{value ? `${value.day}/${value.month}/${value.year}` : 'Select a date'}</span>
              <Icon
                id="icon-datepicker"
                width={20}
                height={20}
                className="text-black group-hover:text-primary"
              />
            </div>
          </Button>
        </Group>

        <Popover className="px-sm rounded-md border bg-white">
          <Dialog>
            <Calendar>
              <header className="!my-0 flex items-center justify-between px-10 py-16">
                <Button slot="previous" className="text-20">
                  <Icon
                    id="icon-chevron-left"
                    width={24}
                    height={24}
                    className="text-black hover:text-primary"
                  />
                  <span className="sr-only">Previous month</span>
                </Button>

                <Heading className="mb-0 font-sans text-16 font-bold" />

                <Button slot="next" className="text-20 text-primary">
                  <Icon
                    id="icon-chevron-right"
                    width={24}
                    height={24}
                    className="text-black hover:text-primary"
                  />
                  <span className="sr-only">Next month</span>
                </Button>
              </header>

              <CalendarGrid className="reset-styles !my-0">
                {(date) => (
                  <CalendarCell date={date} data-today={isToday(date, 'GMT') ? 'true' : 'false'}>
                    {({ formattedDate, isSelected, isDisabled, isOutsideMonth, isUnavailable }) => (
                      <div
                        className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition-colors ${
                          isSelected ? 'font-bold !text-white' : ''
                        } ${
                          isDisabled || isOutsideMonth || isUnavailable
                            ? 'cursor-not-allowed text-grey-400 opacity-80 hover:bg-transparent'
                            : ''
                        } `}>
                        {formattedDate}
                      </div>
                    )}
                  </CalendarCell>
                )}
              </CalendarGrid>
            </Calendar>
          </Dialog>
        </Popover>

        <button
          onClick={() => {
            setValue(null)
            stateCallback(null)
          }}>
          <Icon id="icon-close" width={24} height={24} className="text-black hover:text-primary" />
          <span className="sr-only">Clear date value</span>
        </button>
      </DatePicker>
    </div>
  )
}
